/**
 * `<CalendarPicker>` — an inline month-view calendar for the date-chip
 * popover. Replaces the prior native `<input type="date">` (2026-05-29)
 * for two reasons:
 *
 *   1. Native browsers render `<input type="date">` as a small text
 *      field with a "mm/dd/yyyy" placeholder + a tiny calendar dropdown
 *      that opens in a separate flow. Users expect a visible calendar
 *      grid the moment they click the chip — that's Confluence /
 *      Notion's date-chip UX.
 *   2. The visual chrome of `<input type="date">` varies dramatically
 *      across OS / browser. A custom grid gives us consistent styling
 *      that respects the 3mote token system + 3 themes.
 *
 * The grid:
 *   - 7-column day grid (Sun-Sat).
 *   - 6 rows that hold the current month's days plus tail of previous
 *     month at the head + head of next month at the tail. Days outside
 *     the displayed month are dimmed.
 *   - Header: previous/next month arrows + "Month Year" caption.
 *   - Footer: "Today" + "Clear" actions.
 *   - Today highlighted with a colored ring.
 *   - Selected day filled with the accent color.
 *
 * Locale: `Intl.DateTimeFormat(i18n.language, { weekday: 'short' })`
 * memoized via i18n.language so the day-of-week labels + month name
 * re-render on a locale switch. Per `[FE/i18n]` pitfall, no module-
 * level `Intl.*` singletons.
 *
 * Closing: outside-click and Esc dismiss via the parent NodeView's
 * `onClose` callback.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

export interface CalendarPickerProps {
  /**
   * Currently-selected ISO date (YYYY-MM-DD). `null` is permitted for
   * defensive backwards-compatibility with legacy chips that may have
   * been authored before the default-to-today change (2026-05-29) —
   * the calendar handles a null seed by defaulting the view to the
   * current month.
   */
  date: string | null
  /**
   * Called with the new ISO date when the user picks a day. Clearing
   * a date is no longer a picker concern (the Clear action was
   * removed 2026-05-29) — users delete the whole chip to remove a
   * date.
   */
  onChange: (next: string) => void
  /** Outside-click / Esc dismiss. */
  onClose: () => void
  /**
   * Ref to the trigger element the picker should anchor against
   * (the date chip's button). The picker reads the trigger's
   * `getBoundingClientRect()` on mount + scroll/resize to compute
   * its fixed-position coordinates. Required so the picker can
   * portal to `document.body` and escape clipping ancestors (the
   * doc body's `overflow-y-auto` was clipping the calendar when
   * the chip sat near the viewport bottom).
   */
  anchorRef: RefObject<HTMLElement | null>
}

const POPOVER_WIDTH = 260
/** Approximate popover height — used for the flip-above decision before measure. */
const POPOVER_ESTIMATED_HEIGHT = 320
const GAP_PX = 4

/** Parse `YYYY-MM-DD` into a local-time Date, or null on bad input. */
function parseIsoDate(iso: string | null): Date | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const dt = new Date(`${iso}T00:00`)
  return Number.isNaN(dt.getTime()) ? null : dt
}

/** Serialize a Date to `YYYY-MM-DD` in LOCAL time (matches what the user picked). */
function toIsoDate(dt: Date): string {
  const y = dt.getFullYear()
  const m = (dt.getMonth() + 1).toString().padStart(2, '0')
  const d = dt.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function CalendarPicker({ date, onChange, onClose, anchorRef }: CalendarPickerProps) {
  const { t, i18n } = useTranslation('wiki')
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Viewport-fixed coordinates derived from the anchor's rect. Re-
  // computed on mount, on window scroll, and on window resize so the
  // popover tracks the chip as the doc scrolls underneath. We use
  // fixed positioning (not absolute) so the popover sits in the
  // viewport coordinate system and escapes every overflow-clipping
  // ancestor — including the doc body's `overflow-y-auto`.
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  useEffect(() => {
    const recompute = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      // Prefer below the anchor; flip above when there's no room.
      const wouldOverflowBelow = rect.bottom + POPOVER_ESTIMATED_HEIGHT + GAP_PX > vh
      const fitsAbove = rect.top - POPOVER_ESTIMATED_HEIGHT - GAP_PX > 8
      const top =
        wouldOverflowBelow && fitsAbove
          ? rect.top - POPOVER_ESTIMATED_HEIGHT - GAP_PX
          : Math.min(rect.bottom + GAP_PX, vh - POPOVER_ESTIMATED_HEIGHT - 8)
      // Align left edges; clamp to the viewport so the picker doesn't
      // spill off-screen on narrow viewports / chips near the right
      // edge.
      const desiredLeft = rect.left
      const left = Math.max(8, Math.min(desiredLeft, vw - POPOVER_WIDTH - 8))
      setPosition({ top, left })
    }
    recompute()
    window.addEventListener('scroll', recompute, true)
    window.addEventListener('resize', recompute)
    return () => {
      window.removeEventListener('scroll', recompute, true)
      window.removeEventListener('resize', recompute)
    }
  }, [anchorRef])

  // The month currently displayed. Defaults to the selected date's
  // month if any, otherwise the current month. Year + month kept in a
  // single Date so prev/next operations are a simple +/-1 on the
  // month component.
  const [view, setView] = useState(() => {
    const seed = parseIsoDate(date) ?? new Date()
    return new Date(seed.getFullYear(), seed.getMonth(), 1)
  })

  const today = useMemo(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }, [])

  const selected = useMemo(() => parseIsoDate(date), [date])

  // -- Outside-click + Esc dismiss --------------------------------------------
  // Outside-click also has to ignore the anchor itself — re-clicking
  // the chip should toggle close via the chip's own handler, not
  // bubble through here as an "outside click" and fire onClose twice.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return
      const target = e.target as Node
      if (rootRef.current.contains(target)) return
      if (anchorRef.current?.contains(target)) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose, anchorRef])

  // -- Locale-aware labels ----------------------------------------------------
  // Memoized on `i18n.language` so a runtime locale switch picks up
  // new labels (per the [FE/i18n] Intl-singleton pitfall — never
  // capture locale at module init).
  const dowLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, { weekday: 'narrow' })
    // 2024-09-01 was a Sunday — anchor the 7 weekday labels here.
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(2024, 8, 1 + i)
      return fmt.format(d)
    })
  }, [i18n.language])

  const headerCaption = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' })
    return fmt.format(view)
  }, [i18n.language, view])

  // -- Build the 6×7 day grid -------------------------------------------------
  // Days outside the current month are still rendered so the grid
  // height stays constant — they're dimmed and clickable (clicking
  // them advances the view to that month + picks the day).
  const cells = useMemo(() => {
    const year = view.getFullYear()
    const month = view.getMonth()
    const firstWeekday = new Date(year, month, 1).getDay() // 0=Sun
    const daysInThisMonth = new Date(year, month + 1, 0).getDate()
    const daysInPrevMonth = new Date(year, month, 0).getDate()

    // 6 rows × 7 columns = 42 cells total.
    const out: Array<{ date: Date; inMonth: boolean }> = []

    // Leading days from previous month.
    for (let i = firstWeekday - 1; i >= 0; i--) {
      out.push({ date: new Date(year, month - 1, daysInPrevMonth - i), inMonth: false })
    }
    // Current-month days.
    for (let d = 1; d <= daysInThisMonth; d++) {
      out.push({ date: new Date(year, month, d), inMonth: true })
    }
    // Trailing days from next month — fill up to 42.
    let next = 1
    while (out.length < 42) {
      out.push({ date: new Date(year, month + 1, next), inMonth: false })
      next++
    }
    return out
  }, [view])

  const goPrev = useCallback(() => {
    setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))
  }, [])
  const goNext = useCallback(() => {
    setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))
  }, [])
  const pickDay = useCallback(
    (dt: Date) => {
      onChange(toIsoDate(dt))
      onClose()
    },
    [onChange, onClose],
  )
  const pickToday = useCallback(() => {
    onChange(toIsoDate(today))
    onClose()
  }, [today, onChange, onClose])

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  const ariaLabel = t('date.editAriaLabel', { defaultValue: 'Pick a date' })
  const todayLabel = t('date.todayLabel', { defaultValue: 'Today' })
  const prevLabel = t('date.prevMonthLabel', { defaultValue: 'Previous month' })
  const nextLabel = t('date.nextMonthLabel', { defaultValue: 'Next month' })

  // Render nothing until the position is resolved — avoids a one-
  // frame flash at (0, 0) before the effect computes coords.
  if (position === null) return null

  return createPortal(
    <div
      ref={rootRef}
      contentEditable={false}
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> applies a UA modal layer + focus trap that conflicts with the ProseMirror NodeView's selection model. ARIA role="dialog" on a div is the supported pattern for inline popovers — same shape as `<LinkEditorPopover>` and the slash-menu popover.
      role="dialog"
      aria-label={ariaLabel}
      onMouseDown={(e) => e.stopPropagation()}
      // `fixed` positioning + viewport coords from the anchor rect, so
      // the popover escapes every overflow-clipping ancestor (the doc
      // body's `overflow-y-auto` was clipping when the chip sat near
      // the viewport bottom).
      style={{ position: 'fixed', top: position?.top ?? 0, left: position?.left ?? 0 }}
      className="z-50 w-[260px] rounded-md border border-border-strong bg-bg-elevated p-3 shadow-lg"
    >
      {/* Month nav header */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label={prevLabel}
          onClick={goPrev}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-fg-muted hover:bg-bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <span className="text-sm font-medium text-fg" aria-live="polite">
          {headerCaption}
        </span>
        <button
          type="button"
          aria-label={nextLabel}
          onClick={goNext}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-fg-muted hover:bg-bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>

      {/* Day-of-week labels */}
      <div className="grid grid-cols-7 gap-0.5 pb-1">
        {dowLabels.map((label, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: weekday labels are stable positionally + locale-derived; index is the canonical key.
            key={i}
            className="flex h-6 items-center justify-center text-[10px] font-semibold uppercase tracking-wider text-fg-subtle"
            aria-hidden="true"
          >
            {label}
          </div>
        ))}
      </div>

      {/* 6×7 day grid */}
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((cell) => {
          const isSelected = selected !== null && sameDay(cell.date, selected)
          const isToday = sameDay(cell.date, today)
          const stateClass = isSelected
            ? 'bg-accent text-accent-fg'
            : isToday
              ? 'ring-1 ring-accent text-fg'
              : cell.inMonth
                ? 'text-fg hover:bg-bg-muted'
                : 'text-fg-subtle hover:bg-bg-muted'
          return (
            <button
              key={toIsoDate(cell.date)}
              type="button"
              onClick={() => pickDay(cell.date)}
              aria-label={cell.date.toDateString()}
              aria-pressed={isSelected}
              className={`flex h-8 items-center justify-center rounded text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${stateClass}`}
            >
              {cell.date.getDate()}
            </button>
          )
        })}
      </div>

      {/* Footer — Today shortcut only. Clear was removed 2026-05-29;
          the only way to remove a date chip is to delete the entire
          chip (Backspace from after, or NodeSelection + Delete). */}
      <div className="mt-2 flex items-center justify-end border-t border-border pt-2">
        <button
          type="button"
          onClick={pickToday}
          className="rounded px-2 py-1 text-xs font-medium text-fg-muted hover:bg-bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {todayLabel}
        </button>
      </div>
    </div>,
    document.body,
  )
}
